// SkinPreview — live 3D showcase of a hole skin (home + shop), plus cached
// thumbnails for the shop grid rendered once with an offscreen renderer.
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { HoleVisual } from "../game/holeVisual";
import { Effects } from "../game/particles";
import { SKINS, skinById, type SkinDefinition } from "../game/skins";

/** Ground disc with the hole cut out at the origin (radius 1). */
function cutGround(color: number, radius: number): THREE.Mesh {
  const material = new THREE.MeshStandardMaterial({ color, roughness: 0.9 });
  material.onBeforeCompile = (shader) => {
    shader.vertexShader = `varying vec3 vW;\n${shader.vertexShader}`.replace(
      "#include <worldpos_vertex>",
      "#include <worldpos_vertex>\nvW = (modelMatrix * vec4(transformed, 1.0)).xyz;",
    );
    shader.fragmentShader = `varying vec3 vW;\n${shader.fragmentShader}`.replace(
      "#include <clipping_planes_fragment>",
      "#include <clipping_planes_fragment>\nif (length(vW.xz) < 1.0) discard;\nfloat edge = smoothstep(" + radius.toFixed(1) + ", " + (radius * 0.55).toFixed(1) + ", length(vW.xz));",
    ).replace("#include <dithering_fragment>", "#include <dithering_fragment>\ngl_FragColor.a *= edge;");
  };
  material.transparent = true;
  const mesh = new THREE.Mesh(new THREE.CircleGeometry(radius, 64), material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.receiveShadow = true;
  return mesh;
}

function buildStage(skin: SkinDefinition, effects?: Effects) {
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight(0xe6f2ff, 0x3a4560, 2.2));
  const sun = new THREE.DirectionalLight(0xfff0d6, 3.2);
  sun.position.set(-3, 6, 2);
  sun.castShadow = true;
  sun.shadow.mapSize.set(512, 512);
  scene.add(sun);
  scene.add(cutGround(0x3c4a66, 2.6));
  const visual = new HoleVisual(skin, { effects });
  visual.update(1 / 60, 0, 0, 1);
  scene.add(visual.group);
  if (effects) scene.add(effects.group);
  return { scene, visual };
}

const PROPS = [
  { geo: () => new THREE.BoxGeometry(0.22, 0.14, 0.12), color: 0xff6b5b },
  { geo: () => new THREE.ConeGeometry(0.07, 0.2, 10), color: 0xff9f43 },
  { geo: () => new THREE.CylinderGeometry(0.07, 0.07, 0.16, 10), color: 0x62c6ff },
  { geo: () => new THREE.IcosahedronGeometry(0.1, 0), color: 0x7ce38b },
];

interface PreviewProps {
  skinId: string;
  className?: string;
  /** Feed small props into the hole every few seconds. */
  feed?: boolean;
}

export function SkinPreview({ skinId, className, feed = true }: PreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: "low-power" });
    } catch {
      return;
    }
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.shadowMap.enabled = true;
    renderer.setClearColor(0x000000, 0);
    const effects = new Effects();
    const { scene, visual } = buildStage(skinById(skinId), effects);
    const camera = new THREE.PerspectiveCamera(38, 1, 0.05, 50);

    const resize = () => {
      const w = Math.max(1, canvas.clientWidth);
      const h = Math.max(1, canvas.clientHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      effects.setViewportHeight(h * renderer.getPixelRatio(), camera.fov);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    // Props that slide in from the edge and drop into the hole.
    const props: Array<{ mesh: THREE.Mesh; angle: number; t: number }> = [];
    let nextProp = 0.6;
    const clock = new THREE.Clock();
    let elapsed = 0;
    renderer.setAnimationLoop(() => {
      const dt = Math.min(0.05, clock.getDelta());
      elapsed += dt;
      const orbit = elapsed * 0.25;
      camera.position.set(Math.sin(orbit) * 3.3, 2.5, Math.cos(orbit) * 3.3);
      camera.lookAt(0, -0.25, 0);
      if (feed && elapsed > nextProp) {
        nextProp = elapsed + 1.4;
        const kind = PROPS[Math.floor(Math.random() * PROPS.length)]!;
        const mesh = new THREE.Mesh(kind.geo(), new THREE.MeshStandardMaterial({ color: kind.color, roughness: 0.6 }));
        mesh.castShadow = true;
        scene.add(mesh);
        props.push({ mesh, angle: Math.random() * Math.PI * 2, t: 0 });
      }
      for (let i = props.length - 1; i >= 0; i--) {
        const p = props[i]!;
        p.t += dt;
        const r = Math.max(0, 2.6 - p.t * 1.6);
        const fall = r < 0.75 ? (0.75 - r) : 0;
        const drop = fall > 0 ? (p.t - (2.6 - 0.75) / 1.6) : 0;
        p.mesh.position.set(Math.cos(p.angle) * r, 0.1 - drop * drop * 6, Math.sin(p.angle) * r);
        p.mesh.rotation.set(drop * 6, p.t * 2, drop * 4);
        if (drop > 0 && !p.mesh.userData.bumped) {
          p.mesh.userData.bumped = true;
          visual.bump(0.6);
          visual.burst(6);
        }
        if (p.mesh.position.y < -2.5) {
          scene.remove(p.mesh);
          p.mesh.geometry.dispose();
          (p.mesh.material as THREE.Material).dispose();
          props.splice(i, 1);
        }
      }
      visual.update(dt, 0, 0, 1);
      effects.update(dt);
      renderer.render(scene, camera);
    });

    return () => {
      renderer.setAnimationLoop(null);
      observer.disconnect();
      visual.dispose();
      effects.dispose();
      for (const p of props) { p.mesh.geometry.dispose(); (p.mesh.material as THREE.Material).dispose(); }
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          object.geometry.dispose();
          (Array.isArray(object.material) ? object.material : [object.material]).forEach((m) => m.dispose());
        }
      });
      renderer.dispose();
      renderer.forceContextLoss();
    };
  }, [skinId, feed]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}

let thumbCache: Map<string, string> | undefined;
let thumbPromise: Promise<Map<string, string>> | undefined;

/** Renders every skin once into a small PNG (cached for the session). */
export function skinThumbnails(): Promise<Map<string, string>> {
  if (thumbCache) return Promise.resolve(thumbCache);
  if (thumbPromise) return thumbPromise;
  thumbPromise = new Promise((resolve) => {
    // Let the page paint first.
    setTimeout(() => {
      const map = new Map<string, string>();
      try {
        const canvas = document.createElement("canvas");
        const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, preserveDrawingBuffer: true });
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.setClearColor(0x000000, 0);
        renderer.setSize(200, 200, false);
        const camera = new THREE.PerspectiveCamera(36, 1, 0.05, 50);
        camera.position.set(0, 2.9, 2.2);
        camera.lookAt(0, -0.3, 0);
        for (const skin of SKINS) {
          const { scene, visual } = buildStage(skin);
          for (let i = 0; i < 20; i++) visual.update(1 / 30, 0, 0, 1);
          renderer.render(scene, camera);
          map.set(skin.id, canvas.toDataURL("image/png"));
          visual.dispose();
          scene.traverse((object) => {
            if (object instanceof THREE.Mesh) {
              object.geometry.dispose();
              (Array.isArray(object.material) ? object.material : [object.material]).forEach((m) => m.dispose());
            }
          });
        }
        renderer.dispose();
        renderer.forceContextLoss();
      } catch {
        // No WebGL: the grid falls back to CSS swatches.
      }
      thumbCache = map;
      resolve(map);
    }, 30);
  });
  return thumbPromise;
}

export function useSkinThumbnails() {
  const [thumbs, setThumbs] = useState<Map<string, string>>(() => thumbCache ?? new Map());
  useEffect(() => {
    let alive = true;
    void skinThumbnails().then((map) => { if (alive) setThumbs(map); });
    return () => { alive = false; };
  }, []);
  return thumbs;
}
